import { Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { Spin } from "antd";
import { useEffect } from "react";
import { useAuthStore } from "@/modules/auth/store/auth.store";

/**
 * Autentifikatsiyasiz ochiladigan yo'llar.
 *
 * ⚠️ **`/delete-account` shu ro'yxatda BO'LISHI SHART.** Google Play
 * «Data deletion» talabi hisobni o'chirish so'rovi sahifasini HISOBSIZ
 * ochilishini talab qiladi — tekshiruvchi ilovaga kira olmaydi. Ro'yxatsiz
 * `RootRoute` uni darhol `/login` ga otib yuborardi va do'kondagi havola
 * login ekraniga olib borardi.
 *
 * ⚠️ Ro'yxat KIRISHNI ochadi, `/dashboard` ga yo'naltirishni EMAS: kirgan
 * foydalanuvchi ham bu sahifani o'qiy olishi kerak, shuning uchun
 * «kirgan bo'lsa — panelga» qoidasi faqat `/login` ga tegishli bo'lib
 * qoladi.
 */
export const PUBLIC_PATHS = new Set(["/login", "/delete-account"]);

/**
 * ⚠️ **Oxiridagi `/` ATAYLAB olib tashlanadi.** `PUBLIC_PATHS` to'plami
 * `pathname` ni AYNAN solishtiradi, `RootRoute` esa marshrut daraxti yo'lni
 * normallashtirishidan OLDIN ishga tushadi — ya'ni `/delete-account/`
 * ro'yxatga tushmay `/login` ga otilardi. Brauzerda o'lchangan: `/delete-account`
 * ochilardi, `/delete-account/` esa login ekraniga olib borardi.
 *
 * Bu tasodifiy holat emas: manzil Google Play Console ga QO'LDA kiritiladi
 * va do'kon sahifasidagi havolada oxirgi slash bo'lishi odatiy. Nuqson
 * jimgina bo'lardi — sahifa joyida, testlar yashil, faqat do'kon
 * tekshiruvchisi login ekranini ko'rardi.
 *
 * Ro'yxatga ikkinchi («slash bilan») yozuv QO'SHILMAYDI — har yangi ochiq
 * yo'l uchun ikki yozuvni yodda tutish kerak bo'lardi va biri unutilardi.
 */
export function isPublicPath(pathname: string): boolean {
	const normallashgan = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

	return PUBLIC_PATHS.has(normallashgan);
}

/**
 * Ildiz route — sessiya holatiga qarab yo'naltiradi.
 *
 * Avvalgi marshrutlashtiruvchida bu `ProtectedRoute` ning ishi edi.
 *
 * Nega `beforeLoad` emas, komponent darajasida: `beforeLoad` faqat route
 * hal qilinayotganda ishlaydi — sessiya keyinroq o'zgarsa (token muddati
 * tugashi, boshqa tabda logout) qayta ishlamaydi, shuning uchun baribir
 * `useEffect` kerak bo'lardi. Bundan tashqari `isLoading` holatida
 * `beforeLoad` chalkash bo'lar edi — sekin refresh paytida haqiqiy
 * kirgan foydalanuvchini `/login`ga otib yuborishi mumkin edi.
 */
export function RootRoute() {
	const navigate = useNavigate();
	const location = useLocation();
	const { isAuthenticated, isLoading } = useAuthStore();

	const loginSahifasi = location.pathname === "/login";
	const ochiqSahifa = isPublicPath(location.pathname);

	useEffect(() => {
		if (isLoading) {
			return;
		}
		if (!(isAuthenticated || ochiqSahifa)) {
			navigate({ to: "/login", replace: true });
		}
		if (isAuthenticated && loginSahifasi) {
			navigate({ to: "/dashboard", replace: true });
		}
	}, [isAuthenticated, isLoading, loginSahifasi, ochiqSahifa, navigate]);

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<Spin size="large" />
			</div>
		);
	}

	// `useEffect` faqat paint'dan keyin ishga tushadi — shu oraliqda himoyalangan
	// sahifa (va uning data-fetch effektlari) bir lahzaga bo'lsa ham
	// autentifikatsiyasiz mount bo'lib qolmasligi uchun sinxron ravishda
	// hech narsa render qilmaymiz. `ProtectedRoute` xuddi shu naqshni
	// qo'llaydi (`ruxsatYoq` bo'lsa `null`).
	if (!(isAuthenticated || ochiqSahifa)) {
		return null;
	}

	return <Outlet />;
}
